import { useCallback, useState } from 'react'
import { Controller } from 'react-hook-form'
import { useMutation } from '@tanstack/react-query'

import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import Button from '@/elements/Button'
import Form from '@/elements/Form'
import FormInput from '@/elements/FormInput'
import Modal from '@/components/Modal'
import { setEmailPassword } from '@/api/user'
import toast from '@/utils/toast'
import useAuthStore from '@/stores/authStore'
import useBreakpoints from '@/hooks/useBreakpoints'
import useEmailPasswordForm, {
  type EmailPasswordFormType
} from '@/hooks/forms/useEmailPasswordForm'

function EmailPasswordForm({
  refetchIdentities,
  disconnect,
  hasEmailPasswordIdentity,
  isPrimary
}: {
  refetchIdentities: () => void,
  disconnect: (identityKey: string) => void,
  hasEmailPasswordIdentity: boolean,
  isPrimary: boolean
}) {
  const [showModal, setShowModal] = useState(false)
  const { isMobile } = useBreakpoints()
  const { form } = useEmailPasswordForm()
  const { user } = useAuthStore()

  const { mutateAsync: submitPassword, isPending } = useMutation({
    mutationFn: setEmailPassword,
  })

  const handleSubmit = useCallback(async (formData: EmailPasswordFormType) => {
    try {
      await submitPassword({
        email: user?.email || '',
        password: formData.password
      })

      toast.success('Email & Password login enabled successfully')
      setShowModal(false)
      form.reset()
      refetchIdentities()
    } catch {
      toast.error('Failed to set up Email & Password login')
    }
  }, [submitPassword, user, form, refetchIdentities])

  const handleCancel = useCallback(() => {
    setShowModal(false)
    form.reset()
  }, [form])

  const isSubmitting = isPending

  return (
    <Flex
      inline={!isMobile}
      wfull
      wrap
      justify='space-between'
      align='center'
      style={{ minHeight: 54 }}
      gap={2}
    >
      <Flex gap={1}>
        <Text variant='h4' color='black'>Email & Password</Text>
        {hasEmailPasswordIdentity && (
          <Text variant='textblock' color='black-light-40'>
            {isPrimary ? 'Primary' : 'Connected'}: {user?.email}
          </Text>
        )}
      </Flex>

      {hasEmailPasswordIdentity ? (
        <Button
          variant='danger'
          onClick={() => disconnect('cognito')}
          disabled={isPrimary}
          fullWidth={isMobile}
          styles={{ minWidth: '130px' }}
        >
          Disconnect
        </Button>
      ) : (
        <Button
          variant='secondary'
          onClick={() => setShowModal(true)}
          fullWidth={isMobile}
          styles={{ minWidth: '130px' }}
        >
          Set Up
        </Button>
      )}

      <Modal
        isOpen={showModal}
        onCancel={handleCancel}
        onSave={form.handleSubmit(handleSubmit)}
        title='Set Up Email & Password'
        style={{ width: 538 }}
        saveLabel={isSubmitting ? 'Saving...' : 'Save'}
        disabled={isSubmitting}
      >
        <Form onSubmit={form.handleSubmit(handleSubmit)}>
          <FormInput
            value={user?.email || ''}
            name='email'
            label='Email'
            placeholder='Enter your email'
            type='email'
            disabled
          />
          <Controller
            name='password'
            control={form.control}
            render={({ field }) => (
              <FormInput
                {...field}
                label='Password'
                placeholder='Enter your password'
                type='password'
                required
                disabled={isSubmitting}
                error={form.formState.errors.password?.message}
                dirty={!!form.formState.dirtyFields.password}
              />
            )}
          />
          <Controller
            name='passwordConfirm'
            control={form.control}
            render={({ field }) => (
              <FormInput
                {...field}
                label='Confirm Password'
                placeholder='Re-enter your password'
                type='password'
                required
                disabled={isSubmitting}
                error={form.formState.errors.passwordConfirm?.message}
                dirty={!!form.formState.dirtyFields.passwordConfirm}
              />
            )}
          />
        </Form>
      </Modal>
    </Flex>
  )
}

export default EmailPasswordForm
